/**
 * E-Check timeline corrected to Ejar - written once per contract, with a fingerprint.
 *
 *   echeck_corrections/<CONTRACT NO>  { contract, echeck_from, echeck_to, from, to, fp, by, at, last_updated }
 *
 * E-Check records when the car was CHECKED, which is not when the contract opened or closed; Ejar's
 * pick-up / drop-off is what we go by. The E-Check archive itself is append-only, so the correction
 * is kept beside it: saved the first time History Lens finds a difference, rewritten only when Ejar
 * itself changes its dates (the fingerprint `fp` differs) - never flipping back and forth while
 * data is pulled, and still there when the Ejar record is not loaded.
 */
import { collection, doc, documentId, getDocs, query, serverTimestamp, setDoc, where } from 'firebase/firestore';
import { db } from '../firebase';
import { USE_CONTRACTS_V2 } from './contractsV2';
import { finesService, officeError } from './finesService';

const COL = 'echeck_corrections';
/** Contracts per by-contracts call on the store (the engine's limit is 1000). */
const STORE_CHUNK = 1000;
// The new store (USE_CONTRACTS_V2, owner 27 Sep 2026: "we will not rely on Firebase anymore"): the same records in the
// engine's PostgreSQL (POST /api/store/echeck-corrections/by-contracts, POST /api/store/echeck-corrections - written
// there only when the fingerprint differs, who / when stamped by the engine).

/** Fingerprint of the Ejar values a correction was made from. */
export const correctionFp = (contract, from, to) => `ejar|${String(contract || '').toUpperCase()}|${from || ''}|${to || ''}`;

export const echeckCorrections = {
    /** CONTRACT NO -> stored correction, for these contracts (30 per query). */
    async forContracts(contracts) {
        const ids = [...new Set((contracts || []).map((c) => String(c || '').trim().toUpperCase()).filter(Boolean))];
        const out = new Map();
        if (USE_CONTRACTS_V2) {
            for (let i = 0; i < ids.length; i += STORE_CHUNK) {
                const items = await finesService.storeEcheckCorrections(ids.slice(i, i + STORE_CHUNK)).catch((e) => { throw officeError(e); });
                Object.entries(items || {}).forEach(([id, correction]) => out.set(id, correction));
            }
            return out;
        }
        for (let i = 0; i < ids.length; i += 30) {
            const snap = await getDocs(query(collection(db, COL), where(documentId(), 'in', ids.slice(i, i + 30))));
            snap.forEach((d) => out.set(d.id, d.data()));
        }
        return out;
    },

    /** Save one correction unless the stored one has the same fingerprint. Returns true when written. */
    async save(stored, { contract, echeckFrom, echeckTo, from, to, by }) {
        const id = String(contract || '').trim().toUpperCase();
        if (!id) return false;
        const fp = correctionFp(id, from, to);
        if (stored && stored.fp === fp) return false;
        if (USE_CONTRACTS_V2) {
            const answer = await finesService.storeEcheckCorrectionSave({ contract: id, echeckFrom, echeckTo, from, to })
                .catch((e) => { throw officeError(e); });
            return !!answer?.written;
        }
        await setDoc(doc(db, COL, id), {
            contract: id, echeck_from: echeckFrom || '', echeck_to: echeckTo || '', from: from || '', to: to || '',
            fp, by: by || '', at: serverTimestamp(), last_updated: serverTimestamp(),
        });
        return true;
    },
};

export default echeckCorrections;
